import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { getCurrentDpaVersion } from "../config/dpaConfig";
import { getOrganizationDpaStatus } from "../utils/dpaUtils";

/**
 * Hook to check and manage DPA acceptance status
 * Checks organization-level acceptance (if any owner/admin has accepted)
 */
export const useDpaAcceptance = (organizationId) => {
  const [dpaStatus, setDpaStatus] = useState({
    hasAccepted: false,
    loading: true,
    error: null,
    version: getCurrentDpaVersion(),
  });

  useEffect(() => {
    if (organizationId) {
      checkDpaStatus();
    } else {
      setDpaStatus((prev) => ({ ...prev, loading: false }));
    }
  }, [organizationId]);

  const checkDpaStatus = async () => {
    if (!organizationId) return;

    try {
      setDpaStatus((prev) => ({ ...prev, loading: true, error: null }));

      const status = await getOrganizationDpaStatus(organizationId);

      if (!status) {
        setDpaStatus({
          hasAccepted: false,
          loading: false,
          error: null,
          version: getCurrentDpaVersion(),
        });
        return;
      }

      // Check if organization has accepted (any owner/admin)
      const hasAccepted = status.anyAccepted || false;

      setDpaStatus({
        hasAccepted,
        loading: false,
        error: null,
        version: status.version || getCurrentDpaVersion(),
      });
    } catch (error) {
      console.error("Error checking DPA status:", error);
      // Don't expose database errors to users
      const isDatabaseError = error.message?.includes("schema cache") || 
                              error.message?.includes("function") ||
                              error.code === "42883"; // PostgreSQL function not found
      
      setDpaStatus((prev) => ({
        ...prev,
        loading: false,
        hasAccepted: isDatabaseError ? false : prev.hasAccepted, // Default to false if DB error
        error: isDatabaseError ? null : "Unable to verify DPA status. Please try again later.",
      }));
    }
  };

  const acceptDpa = async () => {
    if (!organizationId) {
      throw new Error("Organization ID is required");
    }

    try {
      const currentVersion = getCurrentDpaVersion();
      
      // Get IP address and user agent for audit trail
      const ipAddress = null; // Could be obtained from server-side if needed
      const userAgent = navigator.userAgent;

      // Note: user_id is no longer passed - backend uses auth.uid() for security
      const { data, error } = await supabase.rpc("accept_dpa", {
        organization_id: organizationId,
        dpa_version: currentVersion,
        ip_address: ipAddress,
        user_agent: userAgent,
      });

      if (error) throw error;

      if (data?.success) {
        const newStatus = {
          hasAccepted: true,
          loading: false,
          error: null,
          version: currentVersion,
          acceptedAt: data.acceptedAt,
        };
        setDpaStatus(newStatus);
        return { success: true, data, status: newStatus };
      } else {
        throw new Error(data?.error || "Failed to accept DPA");
      }
    } catch (error) {
      console.error("Error accepting DPA:", error);
      
      // Don't expose database errors to users
      const isDatabaseError = error.message?.includes("schema cache") || 
                              error.message?.includes("function") ||
                              error.code === "42883"; // PostgreSQL function not found
      
      if (isDatabaseError) {
        throw new Error("The DPA acceptance system is not yet configured. Please contact support.");
      }
      
      throw new Error("Unable to accept the agreement at this time. Please try again later.");
    }
  };

  return {
    ...dpaStatus,
    acceptDpa,
    refresh: checkDpaStatus,
  };
};

